import { forwardRef } from 'react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cn } from '@/lib/cn'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link'
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Blokuje przycisk i pokazuje spinner. */
  loading?: boolean
  /** Rozciąga na całą szerokość kontenera. */
  block?: boolean
  icon?: ReactNode
}

const VARIANTS: Record<ButtonVariant, string> = {
  // granatowy — akcja główna ("Prześlij dokument", "Nowa sprawa" na karcie)
  primary: 'bg-navy-900 text-white border border-navy-900 hover:bg-navy-800 active:bg-navy-900',
  // obrys — akcja poboczna ("Eksport", "Duplikuj")
  secondary: 'bg-card text-ink border border-line hover:bg-[#fbfcfd] active:bg-[#f2f5f7]',
  ghost: 'bg-transparent text-muted border border-transparent hover:bg-[#eef1f4] hover:text-ink',
  danger: 'bg-danger text-white border border-danger hover:opacity-90',
  link: 'bg-transparent border border-transparent text-muted hover:text-ink underline-offset-2 hover:underline px-0',
}

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-[28px] px-2.5 text-micro',
  md: 'h-[34px] px-4 text-base',
  lg: 'h-[40px] px-5 text-md',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading = false, block = false, icon, className, children, disabled, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded font-medium whitespace-nowrap',
        'transition-colors duration-fast focus-visible:am-focus focus:outline-none',
        'disabled:opacity-55 disabled:cursor-not-allowed',
        VARIANTS[variant],
        SIZES[size],
        block && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner /> : icon}
      {children}
    </button>
  )
})

function Spinner() {
  return (
    <span
      aria-hidden
      className="inline-block h-[13px] w-[13px] animate-spin rounded-full border-[1.5px] border-current border-r-transparent opacity-70"
    />
  )
}
